import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Stack,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import QueuePlayNextIcon from '@mui/icons-material/QueuePlayNext';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { format } from 'date-fns';
import {
  normalizeMetadata,
  getLevelColor,
  summarizeQueueMetadata,
  formatMsToSeconds,
  hasFromMeFlag,
  deriveRestauranteId,
  deriveClienteTelefone,
  getStepTheme,
  getElapsedFromWebhookMs,
} from '../pages/logsPage.utils';

export function buildLogsColumns({
  restaurantes = [],
  handleOpenModal,
  setFilterTraceId,
  filterTraceId,
}) {
  return [
  {
    field: 'created_at',
    headerName: 'Data/Hora',
    width: 180,
    valueFormatter: (params) => format(new Date(params.value), 'dd/MM/yyyy HH:mm:ss'),
  },
  {
    field: 'trace_id',
    headerName: 'Trace ID',
    width: 130,
    renderCell: (params) => (
      <Tooltip title={params.value || ''}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.value ? params.value.substring(0, 8) + '...' : '-'}
        </span>
      </Tooltip>
    )
  },
  {
    field: 'restaurante_id',
    headerName: 'Restaurante',
    width: 120,
    valueGetter: (params) => deriveRestauranteId(params.row),
    renderCell: (params) => {
      const rest = restaurantes.find(r => String(r.id) === String(params.value));
      return rest ? rest.nome : (params.value || '-');
    }
  },
  {
    field: 'level',
    headerName: 'Nível',
    width: 100,
    renderCell: (params) => (
      <Chip
        label={params.value}
        color={getLevelColor(params.value)}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 700, letterSpacing: 0.4, px: 0.5, borderRadius: '10px' }}
      />
    ),
  },
  {
    field: 'step',
    headerName: 'Etapa',
    width: 170,
    renderCell: (params) => {
      const theme = getStepTheme(params.value || '');
      return (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: theme.bg,
            color: theme.color,
            fontWeight: 700,
            letterSpacing: 0.3,
            borderRadius: '10px',
            maxWidth: '100%',
            px: 0.5
          }}
        />
      );
    }
  },
  {
    field: 'cliente_telefone',
    headerName: 'Telefone',
    width: 150,
    valueGetter: (params) => deriveClienteTelefone(params.row),
    renderCell: (params) => params.value || '-'
  },
  {
    field: 'duration_ms',
    headerName: 'Duração',
    width: 100,
    renderCell: (params) => (params.value !== null && params.value !== undefined) ? (params.value / 1000).toFixed(2) + ' s' : '-'
  },
  {
    field: 'elapsed_from_webhook_ms',
    headerName: 'Desde Webhook',
    width: 130,
    valueGetter: (params) => getElapsedFromWebhookMs(params.row),
    renderCell: (params) => (params.value !== null && params.value !== undefined)
      ? `${(params.value / 1000).toFixed(2)} s`
      : '-'
  },
  {
    field: 'message',
    headerName: 'Mensagem',
    flex: 1,
    minWidth: 360,
    renderCell: (params) => {
      const metadata = params.row._metadataNormalized || normalizeMetadata(params.row.metadata);
      const metadataBody = metadata.body || {};

      let icon = null;
      let prefix = '';
      let color = 'text.primary';
      let directionIcon = null;
      let messageText = params.value;
      const isIgnored = params.row._isTraceIgnored || metadata?.ignored === true;

      const nestedMessage =
        Array.isArray(metadataBody.messages)
          ? metadataBody.messages.map((msg) => (
            msg?.text?.message ||
            msg?.text?.body ||
            msg?.body ||
            msg?.conversation
          )).find((text) => typeof text === 'string' && text.trim().length > 0)
          : undefined;
      const contentCandidates = [
        metadata.messageContent,
        metadata.message_preview,
        metadataBody.text?.message,
        metadataBody.text?.body,
        metadataBody.body,
        metadataBody.message,
        nestedMessage,
        metadata.failure?.fallback
      ];
      const content = contentCandidates.find((text) => typeof text === 'string' && text.trim().length > 0);
      const isFromMe = hasFromMeFlag(metadata);
      const failureInfo = metadata.failure || {};
      const queueInfo = summarizeQueueMetadata(metadata);
      const traceCount = queueInfo.traceIds?.length || 0;
      const step = params.row.step || '';

      if (content) {
        messageText = content;
      }

      if (params.row.step === 'WEBHOOK_RECEIVED') {
        if (isFromMe) {
          const restaurantWebhookColor = '#0d47a1';
          icon = <StoreIcon fontSize="small" sx={{ mr: 0.5, color: restaurantWebhookColor }} />;
          directionIcon = <ArrowBackIcon fontSize="small" sx={{ mr: 1, color: restaurantWebhookColor }} />;
          prefix = 'Restaurante: ';
          color = restaurantWebhookColor;
        } else {
          icon = <PersonIcon fontSize="small" sx={{ mr: 0.5, color: '#1565c0' }} />;
          directionIcon = <ArrowBackIcon fontSize="small" sx={{ mr: 1, color: '#1565c0' }} />;
          const senderName = metadata?.senderName || metadataBody?.senderName || metadataBody?.pushName || 'Cliente';
          prefix = `${senderName}: `;
          color = '#1565c0';
        }
      } else if (params.row.step.startsWith('ZAPI_SEND') || params.row.step.startsWith('ZAPI_BTN')) {
        const isTimeoutStep = params.row.step === 'ZAPI_SEND_TIMEOUT';
        const isRetryStep = params.row.step === 'ZAPI_SEND_RETRY';
        const isErrorStep = params.row.step === 'ZAPI_SEND_ERROR';
        let zapiColor = '#2e7d32';
        if (isRetryStep) zapiColor = '#ef6c00';
        if (isTimeoutStep || isErrorStep) zapiColor = '#c62828';
        icon = <PhoneIphoneIcon fontSize="small" sx={{ mr: 0.5, color: zapiColor }} />;

        if (params.row.step.endsWith('_START')) {
          directionIcon = <ArrowForwardIcon fontSize="small" sx={{ mr: 1, color: '#2e7d32' }} />;
          prefix = 'Envio Z-API: ';
        } else if (params.row.step.endsWith('_SUCCESS')) {
          directionIcon = <ArrowBackIcon fontSize="small" sx={{ mr: 1, color: '#2e7d32' }} />;
          prefix = 'Resposta Z-API: ';
        } else if (isTimeoutStep) {
          directionIcon = <ErrorOutlineIcon fontSize="small" sx={{ mr: 1, color: zapiColor }} />;
          prefix = 'Z-API Timeout: ';
        } else if (isRetryStep) {
          directionIcon = <AutorenewIcon fontSize="small" sx={{ mr: 1, color: zapiColor }} />;
          prefix = 'Reenvio Z-API: ';
        } else if (isErrorStep) {
          directionIcon = <ErrorOutlineIcon fontSize="small" sx={{ mr: 1, color: zapiColor }} />;
          prefix = 'Erro Z-API: ';
        } else {
          directionIcon = <ArrowForwardIcon fontSize="small" sx={{ mr: 1, color: '#2e7d32' }} />;
          prefix = 'Z-API: ';
        }

        color = zapiColor;
      } else if (step.startsWith('AI')) {
        const aiColor = '#3949ab';
        icon = <SmartToyIcon fontSize="small" sx={{ mr: 1, color: aiColor }} />;
        directionIcon = <ArrowForwardIcon fontSize="small" sx={{ mr: 1, color: aiColor }} />;
        prefix = 'Pipeline AI: ';
        color = aiColor;
      } else if (params.row.step.startsWith('GEMINI')) {
        const isErrorStep = params.row.step === 'GEMINI_ERROR';
        const isTimeoutStep = params.row.step === 'GEMINI_TIMEOUT';
        const isRetryStep = params.row.step === 'GEMINI_RETRY';
        let geminiColor = '#7b1fa2';
        if (isRetryStep) geminiColor = '#ef6c00';
        if (isTimeoutStep || isErrorStep) geminiColor = '#c62828';
        icon = <SmartToyIcon fontSize="small" sx={{ mr: 1, color: geminiColor }} />;
        if (params.row.step === 'GEMINI_REQUEST') {
          directionIcon = <ArrowForwardIcon fontSize="small" sx={{ mr: 1, color: '#7b1fa2' }} />;
          prefix = 'Enviado para AI: ';
        } else if (params.row.step === 'GEMINI_RESPONSE') {
          directionIcon = <ArrowBackIcon fontSize="small" sx={{ mr: 1, color: '#7b1fa2' }} />;
          prefix = 'Resposta da AI: ';
        } else if (isRetryStep) {
          directionIcon = <AutorenewIcon fontSize="small" sx={{ mr: 1, color: geminiColor }} />;
          prefix = 'Requisição Gemini (retry): ';
        } else if (isTimeoutStep) {
          directionIcon = <ErrorOutlineIcon fontSize="small" sx={{ mr: 1, color: geminiColor }} />;
          prefix = 'Timeout Gemini: ';
        } else if (isErrorStep) {
          directionIcon = <ErrorOutlineIcon fontSize="small" sx={{ mr: 1, color: geminiColor }} />;
          prefix = failureInfo.reason ? `Falha Gemini (${failureInfo.reason}): ` : 'Falha Gemini: ';
        }
        color = geminiColor;
      } else if (step.startsWith('PROCESSOR')) {
        const procColor = '#00838f';
        icon = <PrecisionManufacturingIcon fontSize="small" sx={{ mr: 1, color: procColor }} />;
        directionIcon = <AutorenewIcon fontSize="small" sx={{ mr: 1, color: procColor }} />;
        prefix = 'Processador: ';
        color = procColor;
      } else if (step.startsWith('QUEUE')) {
        const queueColor = '#ef6c00';
        icon = <QueuePlayNextIcon fontSize="small" sx={{ mr: 1, color: queueColor }} />;
        directionIcon = <ArrowForwardIcon fontSize="small" sx={{ mr: 1, color: queueColor }} />;
        prefix = 'Fila/Debounce: ';
        color = queueColor;
      } else if (step.startsWith('LOCK')) {
        const lockColor = '#455a64';
        icon = <LockOutlinedIcon fontSize="small" sx={{ mr: 1, color: lockColor }} />;
        directionIcon = <ArrowForwardIcon fontSize="small" sx={{ mr: 1, color: lockColor }} />;
        prefix = 'Lock: ';
        color = lockColor;
      }

      const time = format(new Date(params.row.created_at), 'HH:mm:ss');

      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary', fontFamily: 'monospace' }}>
            {time}
          </Typography>
          {directionIcon}
          {icon}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: prefix ? 'bold' : 'normal', color: color }}>
                {isIgnored ? '🔴 ' : ''}{prefix}
              </Typography>
              <Tooltip title={messageText || ''}>
                <Typography variant="body2" sx={{ ml: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 520 }}>
                  {messageText}
                </Typography>
              </Tooltip>
            </Box>
            {(queueInfo.queue.length > 0 || queueInfo.delayMs !== null || queueInfo.remainingMs !== null) && (
              <Stack
                direction="row"
                spacing={0.75}
                sx={{
                  mt: 0.85,
                  flexWrap: 'wrap',
                  maxWidth: '100%',
                  rowGap: 0.75,
                  columnGap: 0.85,
                  alignItems: 'flex-start',
                }}
              >
                {queueInfo.queue.length > 0 && (
                  <Chip
                    size="small"
                    color="primary"
                    label={`${queueInfo.queue.length} na fila`}
                    sx={{
                      fontWeight: 800,
                      letterSpacing: 0.2,
                      borderRadius: '12px',
                      boxShadow: '0 6px 14px rgba(15,23,42,0.12)',
                      '& .MuiChip-label': { px: 1.3, py: 0.25 },
                    }}
                  />
                )}
                {traceCount > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Traces: ${traceCount}`}
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.2,
                      borderRadius: '12px',
                      borderColor: 'rgba(15,23,42,0.18)',
                      backgroundColor: 'rgba(15,23,42,0.04)',
                      flexShrink: 0,
                      '& .MuiChip-label': { px: 1.2, py: 0.25, display: 'block' },
                    }}
                  />
                )}
                {queueInfo.delayMs !== null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Delay ${formatMsToSeconds(queueInfo.delayMs)}s`}
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.2,
                      borderRadius: '12px',
                      borderColor: 'rgba(239,108,0,0.35)',
                      backgroundColor: 'rgba(239,108,0,0.08)',
                      color: '#c05621',
                      '& .MuiChip-label': { px: 1.2, py: 0.25 },
                    }}
                  />
                )}
                {queueInfo.remainingMs !== null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Restante ${formatMsToSeconds(queueInfo.remainingMs)}s`}
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.2,
                      borderRadius: '12px',
                      borderColor: 'rgba(76,175,80,0.35)',
                      backgroundColor: 'rgba(76,175,80,0.10)',
                      color: '#2e7d32',
                      '& .MuiChip-label': { px: 1.2, py: 0.25 },
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>
        </Box>
      );
    }
  },
  {
    field: 'actions',
    headerName: 'Detalhes',
    width: 120,
    sortable: false,
    renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <IconButton onClick={() => handleOpenModal(params.row)} size="small" aria-label="Ver detalhes do log">
          <VisibilityIcon fontSize="small" />
        </IconButton>
        {params.row.trace_id && (
          <Tooltip title="Filtrar por este Trace ID">
            <IconButton
              onClick={() => setFilterTraceId(params.row.trace_id)}
              size="small"
              color={filterTraceId === params.row.trace_id ? 'primary' : 'default'}
              aria-label="Filtrar por este Trace ID"
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    ),
  },
];
}
